'use client'

interface SeedingMatrixProps {
  organic: number
  suspicious: number
  likelySeeded: number
}

export default function SeedingMatrix({
  organic,
  suspicious,
  likelySeeded,
}: SeedingMatrixProps) {
  const total = organic + suspicious + likelySeeded
  if (total === 0) return null

  const organicPct = Math.round((organic / total) * 100)
  const suspiciousPct = Math.round((suspicious / total) * 100)
  const seededPct = 100 - organicPct - suspiciousPct

  const segments = [
    {
      label: 'Organic',
      count: organic,
      pct: organicPct,
      color: '#22c55e',
    },
    {
      label: 'Suspicious',
      count: suspicious,
      pct: suspiciousPct,
      color: '#FDC304',
    },
    {
      label: 'Likely Seeded',
      count: likelySeeded,
      pct: seededPct,
      color: '#F90B8A',
    },
  ].filter(s => s.count > 0)

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <h2 className='text-lg font-semibold text-foreground mb-4'>
        Organic vs Seeded Breakdown
      </h2>

      {/* Stacked bar */}
      <div className='h-6 rounded-full bg-muted overflow-hidden flex'>
        {segments.map(s => (
          <div
            key={s.label}
            className='h-full transition-all duration-700 ease-out'
            style={{
              width: `${s.pct}%`,
              backgroundColor: s.color,
              minWidth: s.pct > 0 ? '2px' : '0',
            }}
          />
        ))}
      </div>

      {/* Legend */}
      <div className='flex items-center justify-center gap-6 mt-4'>
        {segments.map(s => (
          <div key={s.label} className='flex items-center gap-2'>
            <span
              className='h-3 w-3 rounded-sm'
              style={{ backgroundColor: s.color }}
            />
            <span className='text-xs text-foreground/80'>
              {s.label}: <span className='font-semibold'>{s.count}</span>
              <span className='text-muted-foreground'> ({s.pct}%)</span>
            </span>
          </div>
        ))}
      </div>

      {/* Interpretation */}
      <div className='mt-4 text-center'>
        {organicPct >= 80 && (
          <p className='text-xs text-foreground/60'>
            Healthy distribution. Most mentions appear genuinely organic.
          </p>
        )}
        {organicPct >= 50 && organicPct < 80 && (
          <p className='text-xs text-foreground/60'>
            Mixed signals. Some posts warrant closer inspection for astroturfing
            patterns.
          </p>
        )}
        {organicPct < 50 && (
          <p className='text-xs text-foreground/60'>
            Concerning distribution. A majority of mentions show seeding risk
            indicators.
          </p>
        )}
      </div>
    </div>
  )
}
